import { safeGradient } from "@/shared/utils/gradient-css";
import { isGradient } from "@components/color-picker/lib/detect";
import {
  MEDIA_BLOB_SHAPE,
  resolveParallelogramMask,
} from "@library/constants";
import styled, { css } from "styled-components";
import {
  buildMediaBoxShadow,
  buildMediaDropShadow,
} from "../../../shared/build-media-shadow";
import type { ImageElementProps, ImageLayoutProps } from "../types";

export const ImageContainerStyled = styled.div<ImageLayoutProps>`
  /* Handle box shadow for non-blob/parallelogram shapes */
  ${({ $shadow, $shape = "", $shadowColor, $shadowOpacity }) => {
    if (
      !$shadow ||
      $shadow === "none" ||
      ["blob", "parallelogram"].includes($shape)
    )
      return;
    return css`
      box-shadow: ${buildMediaBoxShadow($shadow, $shadowColor, $shadowOpacity)};
    `;
  }}

  &.imageLayout,
  &.logoLayout {
    /* Base layout properties */
    width: var(--entity-width);
    border-radius: ${({ $round = "0" }) =>
      setDesignSystemVariable($round, "media-corner-radius") || "0"};

    /* Handle filter-based shadows for blob/parallelogram shapes */
    ${({ $shadow, $shape, $shadowColor, $shadowOpacity }) => {
      if (
        !$shadow ||
        $shadow === "none" ||
        !["blob", "parallelogram"].includes($shape)
      )
        return;
      return css`
        filter: ${buildMediaDropShadow($shadow, $shadowColor, $shadowOpacity)};
      `;
    }}

    /* Overlay styling */
    .imageWrapperInner::after {
      opacity: ${({ $overlayOpacity }) =>
        $overlayOpacity === "design-system"
          ? "calc(var(--media-overlay-opacity, 0) * 1%)"
          : `calc(${$overlayOpacity} * 1%)`};
      background: ${({ $overlayColor }) => {
        if ($overlayColor === "design-system")
          return "var(--media-overlay-color, #FFFFFF)";
        if (!$overlayColor) return "#FFFFFF";
        if (isGradient($overlayColor)) {
          return safeGradient($overlayColor) ?? "#FFFFFF";
        }
        return $overlayColor;
      }};
      border-radius: ${({ $round = "0" }) =>
        setDesignSystemVariable($round, "media-corner-radius") || "0"};
    }

    /* Mobile: when capped, use shape-based cap (ignore user resize override). When uncapped, use min(user resize, original). Combo width (e.g. 30% for SideBySide 1/3) is always a hard ceiling. When --combo-mobile-fill-cell (array 3+ per line), ignore entity-width so media fills cell. The whole min() is floored by --entity-mobile-min-width so stacked caps never shrink media to nothing. */
    @media screen and (max-width: 768px) {
      width: max(
        var(--entity-mobile-min-width, 40%),
        min(
          calc(
            var(--entity-mobile-width-override, 100%) *
              (1 - var(--combo-mobile-media-capped, 0)) + 100% *
              var(--combo-mobile-media-capped, 0)
          ),
          calc(
            100% * (1 - var(--combo-mobile-media-capped, 0)) +
              var(--entity-mobile-cap-by-shape, 40%) *
              var(--combo-mobile-media-capped, 0)
          ),
          calc(
            var(--entity-width) * (1 - var(--combo-mobile-fill-cell, 0)) +
              100% * var(--combo-mobile-fill-cell, 0)
          ),
          calc(
            var(--entity-width) * var(--combo-mobile-media-ratio, 1) *
              (1 - var(--combo-mobile-fill-cell, 0)) + 100% *
              var(--combo-mobile-fill-cell, 0)
          )
        )
      ) !important;
    }

    /* Logo: same as above but original width is 25%. When --combo-mobile-fill-cell, use 100%. */
    ${({ $isLogo, $shape, $metadata }) =>
      $isLogo &&
      (["square", "circle"].includes($shape) ||
        ($metadata && $metadata.height > $metadata.width)) &&
      css`
        @media screen and (max-width: 768px) {
          width: max(
            var(--entity-mobile-min-width, 40%),
            min(
              calc(
                var(--entity-mobile-width-override, 100%) *
                  (1 - var(--combo-mobile-media-capped, 0)) + 100% *
                  var(--combo-mobile-media-capped, 0)
              ),
              calc(
                100% * (1 - var(--combo-mobile-media-capped, 0)) +
                  var(--entity-mobile-cap-by-shape, 40%) *
                  var(--combo-mobile-media-capped, 0)
              ),
              calc(
                25% * (1 - var(--combo-mobile-fill-cell, 0)) + 100% *
                  var(--combo-mobile-fill-cell, 0)
              ),
              calc(
                var(--entity-width) * var(--combo-mobile-media-ratio, 1) *
                  (1 - var(--combo-mobile-fill-cell, 0)) + 100% *
                  var(--combo-mobile-fill-cell, 0)
              )
            )
          ) !important;
        }
      `}

    /* Circle shape specific styling */
    ${({ $shape }) =>
      $shape === "circle" &&
      css`
        border-radius: 100% !important;
        .imageWrapperInner::after {
          border-radius: 100% !important;
        }
      `}

    /* Inner wrapper styling */
    .imageWrapperInner {
      /* Shape-specific styling */
      ${({ $shape }) => {
        type ShapeStyles = {
          [key: string]: ReturnType<typeof css>;
        };

        const shapes: ShapeStyles = {
          circle: css`
            border-radius: 100% !important;
            padding-top: 100%;
            .imageWrapperInner::after {
              border-radius: 100% !important;
            }
          `,
          square: css`
            padding-top: 100%;
          `,
          blob: css`
            padding-top: 100%;
          `,
          parallelogram: css`
            padding-top: 100%;
          `,
          "16-9": css`
            padding-top: 56.25%;
          `,
          "axis-resize": css`
            height: var(--entity-height);
            padding-top: var(--entity-padding);
          `,
        };
        return shapes[$shape || ""];
      }}

      /* Border radius handling */
      border-radius: ${({ $round }) =>
        setDesignSystemVariable($round, "media-corner-radius") || "0"};

      /* Apply mask for special shapes */
      ${(props) => handleMask(props)}

      /* Apply same mask to the overlay */
      &::after {
        ${(props) => handleMask(props)}
      }
    }
  }

  // Handle link hover
  &.--link {
    img {
      &:hover {
        transform: scale(calc(var(--entity-intiial-scale) * 1.03));
      }
    }
  }
`;

export const ImageElementStyled = styled.img<ImageElementProps>`
  transform: scale(var(--entity-intiial-scale));
  /* Handle border radius for circular shapes */
  ${({ $round }) =>
    ["100px", "100%"].includes($round) &&
    css`
      border-radius: 100%;
      .imageWrapperInner::after {
        border-radius: 100%;
      }
    `}

  /* Handle black and white filter */
  ${({ $filter }) =>
    $filter === "design-system"
      ? css`
          filter: var(--media-black-and-white-filter, grayscale(0));
        `
      : $filter &&
        css`
          filter: grayscale(1);
        `}
`;

const handleMask = (props: {
  $blobType?: string | undefined;
  $parallelogramDirection?: string | undefined;
  $shape?: string;
  $round?: string | undefined;
}) => {
  const { $blobType, $parallelogramDirection, $shape, $round } = props;
  if ($shape === "blob") {
    const mask = MEDIA_BLOB_SHAPE[$blobType || "blob-1"];
    return css`
      mask-image: ${mask};
      mask-repeat: no-repeat;
      mask-position: center;
      mask-size: cover;
    `;
  }

  if ($shape === "parallelogram") {
    const mask = resolveParallelogramMask($parallelogramDirection, $round);
    return css`
      mask-image: ${mask};
      mask-repeat: no-repeat;
      mask-position: center;
      mask-size: cover;
    `;
  }

  return "";
};

const setDesignSystemVariable = (
  value: string | number | "design-system",
  variable: string,
) => {
  if (value === "design-system") return `var(--${variable})`;
  return value;
};
